import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Lock, FileText, Mail, Film, BookOpen, Layers, Image as ImgIcon, Briefcase, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const MATERIALS = [
  { id: "series-bible", title: "Series Bible", icon: BookOpen },
  { id: "pitch-deck", title: "Pitch Deck", icon: BarChart3 },
  { id: "pilot-script", title: "Pilot Script · 101", icon: FileText },
  { id: "lookbook", title: "Lookbook", icon: ImgIcon },
  { id: "season-one", title: "Season One Overview", icon: Layers },
  { id: "character-guide", title: "Character Guide", icon: Briefcase },
  { id: "investor-materials", title: "Investor Materials", icon: BarChart3 },
];

const STORAGE_KEY = "cb_exec_token";

const ExecutiveMaterials = () => {
  const [granted, setGranted] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t) setGranted(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/executive/login", { password });
      if (res.data?.token) {
        localStorage.setItem(STORAGE_KEY, res.data.token);
        setGranted(true);
        toast.success("Access granted.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid access code.");
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setGranted(false);
    setPassword("");
  };

  const handleDownload = (m) => {
    toast.info(`${m.title} — file will be released to verified partners. Reply to your approval email to request delivery.`);
  };

  if (!granted) {
    return (
      <div data-testid="page-materials-gate" className="min-h-screen flex items-center justify-center relative">
        <div className="hero-bg" />
        <div className="hero-grain absolute inset-0" />
        <div className="relative z-10 w-full max-w-md px-6 fade-up">
          <div className="text-center mb-10">
            <div className="font-caps-wide text-[11px] text-[var(--cb-gold)] mb-3">CHERUBIM</div>
            <div className="font-headline text-[28px] text-[var(--cb-text)] uppercase">Executive Materials</div>
            <div className="font-caps text-[10px] text-[var(--cb-text-muted)] mt-3">CLEARANCE REQUIRED</div>
          </div>
          <form data-testid="materials-login-form" onSubmit={submit} className="cb-card p-8">
            <label className="font-caps text-[10px] text-[var(--cb-text-dim)] flex items-center gap-2">
              <Lock size={11} /> ACCESS CODE
            </label>
            <input
              data-testid="materials-password"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Provided in your approval email"
              className="cb-input mt-3"
            />
            {error && (
              <div data-testid="materials-error" className="font-caps text-[11px] text-red-400 mt-3">{error}</div>
            )}
            <button data-testid="materials-submit" type="submit" disabled={loading} className="btn-primary-cb w-full mt-6">
              {loading ? "VERIFYING…" : "REQUEST CLEARANCE"}
            </button>
            <div className="font-caps text-[10px] text-[var(--cb-text-muted)] mt-6 text-center">
              FOR VERIFIED INDUSTRY PARTNERS ONLY
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-materials" className="min-h-screen">
      <section className="pt-16 pb-10 border-b border-white/5">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-8 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-caps-wide text-[11px] text-[var(--cb-gold)]">CHERUBIM</div>
            <h1 className="font-headline text-[36px] md:text-[48px] text-[var(--cb-text)] uppercase mt-2">Executive Materials</h1>
          </div>
          <button data-testid="materials-logout" onClick={signOut} className="btn-outline-cb !py-2 !px-4 !text-[10px]">SIGN OUT</button>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MATERIALS.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  data-testid={`material-${m.id}`}
                  onClick={() => handleDownload(m)}
                  className="cb-card text-left p-6 flex items-start gap-4"
                >
                  <div className="w-11 h-11 rounded-full border border-[var(--cb-gold)]/40 flex items-center justify-center text-[var(--cb-gold)] shrink-0">
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-caps text-[10px] text-[var(--cb-text-muted)]">DOSSIER</div>
                    <div className="font-headline text-[var(--cb-text)] text-[19px] mt-1 uppercase tracking-[0.02em]">{m.title}</div>
                  </div>
                  <Mail size={16} className="text-[var(--cb-gold)] shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Executive-only: the Dampening mechanic sequence — never shown publicly */}
      <section className="pb-16">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-8">
          <div className="font-caps text-[10px] text-[var(--cb-text-muted)] tracking-[0.3em] text-center">EXECUTIVE PREVIEW</div>
          <div className="font-headline text-[var(--cb-text)] text-[20px] md:text-[24px] uppercase text-center mt-3 tracking-[0.04em]">
            The Dampening — Mechanic Sequence
          </div>
          <div className="mt-8 border border-[rgba(197,160,89,0.3)] bg-[#0C0C0E] p-[6px]" data-testid="exec-dampening-sequence">
            <img src="/assets/art/archive-dampening-stages.jpg"
                 alt="The Dampening mechanic sequence — Alignment, Friction, Drag, Dampened"
                 className="block w-full h-auto" loading="lazy" decoding="async" />
          </div>
          <div className="font-caps text-[10px] text-[var(--cb-text-muted)] mt-4 text-center tracking-[0.2em]">
            CONFIDENTIAL — VERIFIED INDUSTRY PARTNERS ONLY
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExecutiveMaterials;
